/**
 * Top-level screen selection: which full-screen mode the app is in (a standalone info/legal page,
 * the admin console, a paywall, the landing page, the partner portal, or the signed-in main app
 * shell) and the overlays (account modal, partner onboarding wizard) that can appear across them.
 * Extracted out of App.tsx, which owns all the state this reads and builds the pre-rendered
 * overlay nodes (registrationOverlay/inviteNoticeToast/chatWidget) it's handed.
 */
import React from 'react';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { AlertTriangle } from 'lucide-react';
import GeneralAdvice from '../pages/GeneralAdvice.tsx';
import type { GeneralAdviceProps } from '../pages/GeneralAdvice.tsx';
import SecurityPage from '../pages/SecurityPage.tsx';
import PrivacyPage from '../pages/PrivacyPage.tsx';
import CostPage from '../pages/CostPage.tsx';
import FaqPage from '../pages/FaqPage.tsx';
import ExperiencePage from '../pages/ExperiencePage.tsx';
import AdminConsole from '../features/admin/AdminConsole.tsx';
import ComplianceConsole from '../features/admin/ComplianceConsole.tsx';
import ApiDocsConsole from '../features/admin/ApiDocsConsole.tsx';
import Paywall from '../components/billing/Paywall.tsx';
import PartnerSubscribe from '../components/billing/PartnerSubscribe.tsx';
import AddClientPaywall from '../components/billing/AddClientPaywall.tsx';
import LandingPage from '../pages/LandingPage.tsx';
import AccountModal from '../components/auth/AccountModal.tsx';
import { setSignInDoor } from '../services/signInDoor.ts';
import MainAppLayout from '../components/layout/MainAppLayout.tsx';
import type { MenuPlan, MenuCaseStudy } from '../components/layout/clientMenu.ts';
import PartnerOnboardingWizard, { clearPartnerWizardDraft } from '../features/professionalPortal/PartnerOnboardingWizard.tsx';
import { getSessionUser } from '../services/authClient.ts';
import type { SessionUser } from '../services/authClient.ts';
import type { PartnerBillingSummary } from '../services/billingApi.ts';
import type { LoadedClientProfile } from '../services/profileApi.ts';
import type { DraftFactFind } from '../constants/initialFactFind.ts';
import type { PartnerFirmState, AppProfessionalCase } from '../features/professionalPortal/useProfessionalPortal.ts';

export interface AppRouterProps {
  // Standalone info/legal screens (each is its own full-screen early-return mode).
  showGeneralAdvice: string | null;
  setShowGeneralAdvice: Dispatch<SetStateAction<string | null>>;
  showSecurity: boolean;
  setShowSecurity: Dispatch<SetStateAction<boolean>>;
  showPrivacy: boolean;
  setShowPrivacy: Dispatch<SetStateAction<boolean>>;
  showCost: boolean;
  setShowCost: Dispatch<SetStateAction<boolean>>;
  showFaqs: boolean;
  setShowFaqs: Dispatch<SetStateAction<boolean>>;
  showExperience: boolean;
  setShowExperience: Dispatch<SetStateAction<boolean>>;

  // Platform admin console.
  showAdmin: boolean;
  setShowAdmin: Dispatch<SetStateAction<boolean>>;
  isPlatformAdmin: boolean;
  /** Licensee oversight: advice given under our licence (server decides who may). */
  canReviewAdvice: boolean;
  showCompliance: boolean;
  setShowCompliance: Dispatch<SetStateAction<boolean>>;
  showApiDocs: boolean;
  setShowApiDocs: Dispatch<SetStateAction<boolean>>;
  closeAdmin: () => void;
  /** The admin landed on the console as home: its link signs out rather than going "back". */
  adminIsHome: boolean;

  // Billing gates.
  showPaywall: boolean;
  partnerPaywall: { firmName: string; tenantId: string } | null;

  // Core routing.
  activePage: string;
  setActivePage: Dispatch<SetStateAction<string>>;
  renderPageContent: () => ReactNode;

  // Account modal / sign-in-sign-out.
  setShowRegistration: Dispatch<SetStateAction<boolean>>;
  showAccountModal: boolean;
  setShowAccountModal: Dispatch<SetStateAction<boolean>>;
  accountModalMode: string | null;
  resetToken: string | null;
  closeAccountModal: () => void;
  resumeFromSignIn: (loaded: LoadedClientProfile | null) => Promise<void>;
  handleSignedOut: () => Promise<void>;
  setFactFind: Dispatch<SetStateAction<DraftFactFind>>;
  setAccountUser: Dispatch<SetStateAction<SessionUser | null>>;

  // Partner door / professional-portal entry.
  showPartnerEntry: boolean;
  setShowPartnerEntry: Dispatch<SetStateAction<boolean>>;
  setActiveProfessionalCaseId: Dispatch<SetStateAction<string | null>>;
  partnerIntent: boolean;
  setPartnerIntent: Dispatch<SetStateAction<boolean>>;
  partnerFirm: PartnerFirmState | null;
  setPartnerFirm: Dispatch<SetStateAction<PartnerFirmState | null>>;
  setProfessionalCases: Dispatch<SetStateAction<AppProfessionalCase[]>>;
  showPartnerWizard: boolean;
  setShowPartnerWizard: Dispatch<SetStateAction<boolean>>;
  setPartnerResolving: Dispatch<SetStateAction<boolean>>;
  portalEntryError: boolean;
  setPortalEntryError: Dispatch<SetStateAction<boolean>>;
  setPortalRefreshNonce: Dispatch<SetStateAction<number>>;
  addClientPaywall: { summary: PartnerBillingSummary | null; tenantId: string } | null;
  setAddClientPaywall: Dispatch<SetStateAction<{ summary: PartnerBillingSummary | null; tenantId: string } | null>>;

  // Main app shell (signed-in consumer / adviser-in-client-journey layout).
  clientProfileStep: number;
  setClientProfileStep: Dispatch<SetStateAction<number>>;
  activeProfessionalCaseId: string | null;
  // What the client menu reads (components/layout/clientMenu.ts): the file's name, how far the
  // profile is, which goals are ticked, what has been saved, and the case's own name for the client.
  factFind: DraftFactFind;
  savedStrategies: readonly MenuPlan[];
  savedCaseStudies: readonly MenuCaseStudy[];
  activeProfessionalCaseName: string | null;

  // Pre-built overlay nodes — each depends on state (factFind, chat messages, ...) that stays in
  // App.tsx, so App.tsx builds the element and hands it down rather than this component needing
  // that whole state surface too.
  registrationOverlay: ReactNode;
  inviteNoticeToast: ReactNode;
  chatWidget: ReactNode;
}

export default function AppRouter({
  showGeneralAdvice,
  setShowGeneralAdvice,
  showSecurity,
  setShowSecurity,
  showPrivacy,
  setShowPrivacy,
  showCost,
  setShowCost,
  showFaqs,
  setShowFaqs,
  showExperience,
  setShowExperience,
  showAdmin,
  setShowAdmin,
  isPlatformAdmin,
  canReviewAdvice,
  showCompliance,
  setShowCompliance,
  showApiDocs,
  setShowApiDocs,
  closeAdmin,
  adminIsHome,
  showPaywall,
  partnerPaywall,
  activePage,
  setActivePage,
  renderPageContent,
  setShowRegistration,
  showAccountModal,
  setShowAccountModal,
  accountModalMode,
  resetToken,
  closeAccountModal,
  resumeFromSignIn,
  handleSignedOut,
  setFactFind,
  setAccountUser,
  showPartnerEntry,
  setShowPartnerEntry,
  setActiveProfessionalCaseId,
  partnerIntent,
  setPartnerIntent,
  partnerFirm,
  setPartnerFirm,
  setProfessionalCases,
  showPartnerWizard,
  setShowPartnerWizard,
  setPartnerResolving,
  portalEntryError,
  setPortalEntryError,
  setPortalRefreshNonce,
  addClientPaywall,
  setAddClientPaywall,
  clientProfileStep,
  setClientProfileStep,
  activeProfessionalCaseId,
  factFind,
  savedStrategies,
  savedCaseStudies,
  activeProfessionalCaseName,
  registrationOverlay,
  inviteNoticeToast,
  chatWidget,
}: AppRouterProps) {
  if (showGeneralAdvice) {
    return (
      <>
        <GeneralAdvice
          // showGeneralAdvice is a plain string (matches LandingPage's loose setShowGeneralAdvice
          // signature); GeneralAdvice.tsx's own section prop is the strict keyof-lookup type — the
          // values that ever land here always come from LANDING_PAGE topic ids that match it.
          section={showGeneralAdvice as GeneralAdviceProps['section']}
          onBack={() => setShowGeneralAdvice(null)}
        />
        {registrationOverlay}
      </>
    );
  }

  if (showSecurity) {
    return <SecurityPage onBack={() => setShowSecurity(false)} />;
  }

  if (showPrivacy) {
    return (
      <PrivacyPage
        onBack={() => setShowPrivacy(false)}
        onOpenSecurity={() => {
          setShowPrivacy(false);
          setShowSecurity(true);
        }}
      />
    );
  }

  if (showCost) {
    return (
      <CostPage
        onBack={() => setShowCost(false)}
        onGetStarted={() => {
          setShowCost(false);
          setShowRegistration(true);
        }}
        onBecomePartner={() => {
          setShowCost(false);
          setActiveProfessionalCaseId(null);
          setShowPartnerEntry(true);
        }}
      />
    );
  }

  if (showFaqs) {
    return (
      <FaqPage
        onBack={() => setShowFaqs(false)}
        onGetStarted={() => {
          setShowFaqs(false);
          setShowRegistration(true);
        }}
        onBecomePartner={() => {
          setShowFaqs(false);
          setActiveProfessionalCaseId(null);
          setShowPartnerEntry(true);
        }}
      />
    );
  }

  if (showExperience) {
    return (
      <ExperiencePage
        onBack={() => setShowExperience(false)}
        onGetStarted={() => {
          setShowExperience(false);
          setShowRegistration(true);
        }}
        onBecomePartner={() => {
          setShowExperience(false);
          setActiveProfessionalCaseId(null);
          setShowPartnerEntry(true);
        }}
      />
    );
  }

  if (showCompliance && canReviewAdvice) {
    return <ComplianceConsole onBack={() => setShowCompliance(false)} />;
  }

  if (showAdmin && isPlatformAdmin) {
    return showApiDocs
      ? <ApiDocsConsole onBack={() => setShowApiDocs(false)} />
      : (
        <AdminConsole
          onBack={adminIsHome ? () => { void handleSignedOut(); } : closeAdmin}
          backLabel={adminIsHome ? 'Sign out' : 'Back'}
          onOpenApiDocs={() => setShowApiDocs(true)}
          onOpenCompliance={canReviewAdvice ? () => setShowCompliance(true) : undefined}
        />
      );
  }

  if (showPaywall) {
    return (
      <Paywall
        onSignOut={handleSignedOut}
        notice="Your account is ready. One step to unlock your full plan and get started."
      />
    );
  }

  if (partnerPaywall) {
    return (
      <PartnerSubscribe
        firmName={partnerPaywall.firmName}
        tenantId={partnerPaywall.tenantId}
        onSignOut={handleSignedOut}
      />
    );
  }

  if (activePage === 'LandingPage') {
    return (
      <>
        <LandingPage
          setActivePage={setActivePage}
          setShowGeneralAdvice={setShowGeneralAdvice}
          setShowRegistration={setShowRegistration}
          onOpenSecurity={() => setShowSecurity(true)}
          onOpenPrivacy={() => setShowPrivacy(true)}
          onOpenCost={() => setShowCost(true)}
          onOpenExperience={() => setShowExperience(true)}
          onOpenFaqs={() => setShowFaqs(true)}
          onProfessionalLogin={() => {
            setActiveProfessionalCaseId(null);
            setShowPartnerEntry(true);
          }}
          onClientLogin={() => {
            // The other half of the rule: through the client door you land on the client
            // dashboard, or wherever you left off — even if you also happen to own a firm.
            setSignInDoor('client');
            setShowAccountModal(true);
          }}
        />
        {showAccountModal && (
          <AccountModal
            initialMode={accountModalMode}
            resetToken={resetToken}
            context={partnerIntent ? 'partner' : undefined}
            onClose={closeAccountModal}
            setFactFind={setFactFind}
            onSignedIn={resumeFromSignIn}
            onSignOut={handleSignedOut}
          />
        )}
        {registrationOverlay}
        {inviteNoticeToast}
        {/* Partner door: sign in to an existing firm, start onboarding, or view the demo */}
        {showPartnerEntry && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[10000] p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-3">
              <h2 className="text-xl font-bold text-gray-900">Professional partners</h2>
              <p className="text-sm text-gray-600">Work with your clients in your firm&apos;s own secure workspace.</p>
              <button
                type="button"
                className="w-full bg-blue-600 text-white font-semibold py-2.5 rounded-lg hover:bg-blue-700 transition-colors"
                onClick={() => {
                  setShowPartnerEntry(false);
                  setPartnerIntent(true);
                  // Remembered for the tab, so a reload — and the auto-resume that races this
                  // sign-in — both still know this person came through the partner door.
                  setSignInDoor('partner');
                  setShowAccountModal(true);
                }}
              >
                Partner sign in
              </button>
              <button
                type="button"
                className="w-full bg-white text-blue-600 border-2 border-blue-600 font-semibold py-2 rounded-lg hover:bg-blue-50 transition-colors"
                onClick={() => {
                  setShowPartnerEntry(false);
                  setShowPartnerWizard(true);
                }}
              >
                Become a partner
              </button>
              <button
                type="button"
                className="w-full text-sm text-gray-400 hover:text-gray-600"
                onClick={() => setShowPartnerEntry(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {showPartnerWizard && (
          <PartnerOnboardingWizard
            onClose={() => {
              clearPartnerWizardDraft();
              setShowPartnerWizard(false);
            }}
            onComplete={async () => {
              setShowPartnerWizard(false);
              setPartnerResolving(true); // branded loader, never the demo login, from frame 1
              // The wizard signs the user in itself (its own signUp/verifySignupCode calls), so
              // App-level accountUser is still null at this point — without this, the real-mode
              // resolution effect below (gated on accountUser) never runs and the workspace loader
              // spins forever.
              setAccountUser(await getSessionUser());
              setActivePage('PartnerPortal');
            }}
          />
        )}
      </>
    );
  }

  if (activePage === 'PartnerPortal') {
    return (
      <>
        {renderPageContent()}
        {inviteNoticeToast}
        {/* Couldn't resolve the workspace (server waking / network blip): retry — never a dead screen */}
        {portalEntryError && (
          <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-white p-6">
            <div className="w-full max-w-sm space-y-4 text-center">
              <AlertTriangle size={36} className="mx-auto text-amber-500" aria-hidden="true" />
              <h2 className="text-lg font-bold text-gray-900">We couldn&apos;t open your workspace</h2>
              <p className="text-sm leading-relaxed text-gray-600">
                The connection hiccupped (this can happen when the server is waking up). Your data is safe
                — give it another go.
              </p>
              <button
                type="button"
                onClick={() => {
                  setPortalEntryError(false);
                  setPortalRefreshNonce((n) => n + 1); // re-run workspace resolution
                }}
                className="w-full rounded-xl bg-gradient-to-r from-[#1a6fd4] to-[#17b8e0] py-2.5 font-semibold text-white shadow-lg shadow-blue-600/25 transition hover:shadow-xl"
              >
                Try again
              </button>
              <button type="button" onClick={handleSignedOut} className="w-full text-sm text-gray-500 transition hover:text-gray-700">
                Sign out instead
              </button>
            </div>
          </div>
        )}
        {showPartnerWizard && (
          <PartnerOnboardingWizard
            onClose={() => {
              clearPartnerWizardDraft();
              setShowPartnerWizard(false);
              // No firm yet and they've declined onboarding — leaving them on the portal page
              // would strand them on the workspace loader. Back to the front door instead.
              if (!partnerFirm) setActivePage('LandingPage');
            }}
            onComplete={() => {
              setShowPartnerWizard(false);
              setPortalRefreshNonce((n) => n + 1); // re-resolve the workspace in real mode
            }}
          />
        )}
        {/* Account/security panel — only when deliberately opened from the portal */}
        {showAccountModal && (
          <AccountModal
            initialMode={accountModalMode}
            resetToken={resetToken}
            context={partnerIntent ? 'partner' : undefined}
            onClose={closeAccountModal}
            setFactFind={setFactFind}
            onSignOut={handleSignedOut}
          />
        )}
        {/* Per-client billing: out of paid capacity when taking on a client → pay or upgrade */}
        {addClientPaywall && (
          <AddClientPaywall
            summary={addClientPaywall.summary}
            tenantId={addClientPaywall.tenantId}
            onClose={() => setAddClientPaywall(null)}
          />
        )}
      </>
    );
  }

  return (
    <>
      <MainAppLayout
        activePage={activePage}
        setActivePage={setActivePage}
        clientProfileStep={clientProfileStep}
        setClientProfileStep={setClientProfileStep}
        showReturnToPartnerPortal={Boolean(activeProfessionalCaseId)}
        onReturnToPartnerPortal={() => setActivePage('PartnerPortal')}
        onSignOut={handleSignedOut}
        // Account/Admin live in the sidebar's bottom action cluster (not floating pills) — one nav
        // language, no overlap. Hidden inside an adviser's client journey (the portal owns that chrome).
        showAccount={!activeProfessionalCaseId}
        onOpenAccount={() => setShowAccountModal(true)}
        showAdmin={isPlatformAdmin && !activeProfessionalCaseId}
        onOpenAdmin={() => setShowAdmin(true)}
        showCompliance={canReviewAdvice && !activeProfessionalCaseId}
        onOpenCompliance={() => setShowCompliance(true)}
        factFind={factFind}
        savedStrategies={savedStrategies}
        savedCaseStudies={savedCaseStudies}
        professionalCaseId={activeProfessionalCaseId}
        clientFileFallbackName={activeProfessionalCaseName}
      >
        {renderPageContent()}
      </MainAppLayout>
      {showAccountModal && (
        <AccountModal
          initialMode={accountModalMode}
          resetToken={resetToken}
          context={partnerIntent ? 'partner' : undefined}
          onClose={closeAccountModal}
          setFactFind={setFactFind}
          onSignOut={handleSignedOut}
        />
      )}
      {/* The onboarding wizard must be reachable on EVERY screen (confirmation links can land anywhere) */}
      {showPartnerWizard && (
        <PartnerOnboardingWizard
          onClose={() => {
            clearPartnerWizardDraft();
            setShowPartnerWizard(false);
          }}
          onComplete={() => {
            setShowPartnerWizard(false);
            setPartnerResolving(true); // branded loader, never the demo login, from frame 1
            setActivePage('PartnerPortal');
          }}
        />
      )}
      {chatWidget}
      {/* Registration overlay - available on all pages */}
      {registrationOverlay}
      {inviteNoticeToast}
    </>
  );
}
